import { ResolvedMedia } from '@longy/common/cms'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { ReactElement } from 'react'

import { useCmsTranslations, usePortalContainer } from '../context/RendererContext'
import { CmsCarousel } from './CmsCarousel'
import { CmsImage } from './CmsImage'

export type CmsLightboxProps = {
    images: Array<{ id: string; media: ResolvedMedia | null | undefined; caption?: string }>
    /** The index the overlay opens on; `null` means closed. */
    openIndex: number | null
    onClose: () => void
    title?: string
}

/**
 * The full-screen image overlay.
 *
 * It portals into `portalContainer` rather than `document.body`, because in the admin the renderers
 * run inside an iframe: mounting on the host document would put the overlay behind the editor
 * chrome, outside the document whose stylesheets style it.
 *
 * The radix primitives are used directly instead of the shared `DialogContent`, which hardcodes its
 * own portal and a centred card width — neither of which a full-bleed lightbox wants.
 */
export const CmsLightbox = ({
    images,
    openIndex,
    onClose,
    title,
}: CmsLightboxProps): ReactElement | null => {
    const portalContainer = usePortalContainer()
    const translations = useCmsTranslations()

    if (images.length === 0) return null

    return (
        <DialogPrimitive.Root open={openIndex !== null} onOpenChange={(next) => !next && onClose()}>
            <DialogPrimitive.Portal container={portalContainer ?? undefined}>
                <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/90" />
                <DialogPrimitive.Content
                    aria-label={title || translations.gallery.open}
                    aria-describedby={undefined}
                    className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 flex items-center justify-center p-4 focus:outline-none sm:p-10"
                >
                    <DialogPrimitive.Title className="sr-only">
                        {title || translations.gallery.open}
                    </DialogPrimitive.Title>

                    <div className="w-full max-w-5xl">
                        <CmsCarousel
                            slidesToShow={1}
                            startIndex={openIndex ?? 0}
                            ariaLabel={title || undefined}
                            slides={images.map((image, index) => ({
                                key: image.id,
                                content: (
                                    <figure className="flex flex-col items-center gap-3">
                                        <CmsImage
                                            media={image.media}
                                            sizes="(min-width: 1024px) 64rem, 100vw"
                                            draggable={false}
                                            className="max-h-[70vh] w-auto rounded-lg object-contain"
                                        />
                                        <figcaption className="text-center text-sm text-white/80">
                                            {image.caption ||
                                                translations.gallery.imagePosition(
                                                    index + 1,
                                                    images.length,
                                                )}
                                        </figcaption>
                                    </figure>
                                ),
                            }))}
                        />
                    </div>

                    <DialogPrimitive.Close
                        aria-label={translations.gallery.close}
                        className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-white/90 text-neutral-900 transition-colors hover:bg-white"
                    >
                        <X className="size-5" />
                    </DialogPrimitive.Close>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    )
}
